import { buildCharacterProfile } from '../engine/yuragi';
import type { CaseDefinition, CharacterDefinition } from '../game/types';

const ASSET_BASE = import.meta.env.BASE_URL;

function character(
  definition: Omit<CharacterDefinition, 'personality' | 'stateHash' | 'yuragiGenesisTick'>,
  tick: number,
): CharacterDefinition {
  return {
    ...definition,
    yuragiGenesisTick: tick,
    ...buildCharacterProfile(definition.identity, tick),
  };
}

export const CASE_001: CaseDefinition = {
  id: 'EC-001',
  seed: 'hotei|glass-archive|v1',
  title: 'THE GLASS ARCHIVE',
  subtitle: 'A sealed room. A false timestamp. A client who is lying.',
  charge: 'Murder of Archive Director Orin Sable',
  victim: 'Orin Sable',
  incidentTime: '21:17 / Archive Nine',
  stakes: 'A guilty verdict erases Lyra’s legal identity and licenses the state to destroy every memory she carries.',
  fairPlayPromise: 'Every decisive conclusion is supported by at least two independent facts. A lie can damage credibility, but it never substitutes for proof.',
  realityAnchors: [
    {
      label: 'Digital timestamps',
      detail: 'A system log is only as reliable as its clock source, calibration history, and audit trail.',
    },
    {
      label: 'Chain of custody',
      detail: 'A sealed object can identify who controlled it even when a witness gives a misleading account.',
    },
    {
      label: 'Credential misuse',
      detail: 'Authorized access does not make an action lawful; privileged systems can conceal abuse as easily as they prevent it.',
    },
  ],
  characters: [
    character(
      {
        id: 'lyra',
        name: 'Lyra Vale',
        role: 'Memory curator · Your client',
        shortRole: 'The accused',
        identity: 'hotei|lyra-vale',
        portrait: `${ASSET_BASE}assets/characters/lyra-vale.png`,
        model: `${ASSET_BASE}assets/models/rocketbox/lyra/lyra.fbx`,
        gender: 'female',
        voice: { pitch: 1.02, rate: 0.94, preferredNames: ['Aria', 'Sonia', 'Zira', 'Samantha'] },
        archiveVisible: true,
        courtVisible: true,
        tint: '#26313a',
        accent: '#d7aa64',
        summary: 'Precise, exhausted, and guarding a truth that could erase her legal personhood.',
        tell: 'She touches the amber pin when a memory matters more than the answer.',
        position: [-4.8, 0, 4.4],
        rotation: 2.5,
      },
      184,
    ),
    character(
      {
        id: 'rook',
        name: 'Rook Halden',
        role: 'Security prefect · Senior witness',
        shortRole: 'The warden',
        identity: 'hotei|rook-halden',
        portrait: `${ASSET_BASE}assets/characters/rook-halden.png`,
        model: `${ASSET_BASE}assets/models/rocketbox/rook/rook.fbx`,
        gender: 'male',
        voice: { pitch: 0.72, rate: 0.88, preferredNames: ['Guy', 'David', 'Daniel', 'Alex'] },
        archiveVisible: true,
        courtVisible: true,
        tint: '#151a1e',
        accent: '#a8b5bf',
        summary: 'Controlled, decorated, and authorized to touch every security system in Archive Nine.',
        tell: 'His right hand closes over a silver cuff when his authority is challenged.',
        position: [5.1, 0, 3.7],
        rotation: -2.6,
      },
      221,
    ),
    character(
      {
        id: 'iona',
        name: 'Iona Mercer',
        role: 'Systems auditor · First discoverer',
        shortRole: 'The auditor',
        identity: 'hotei|iona-mercer',
        portrait: `${ASSET_BASE}assets/characters/iona-mercer.png`,
        model: `${ASSET_BASE}assets/models/rocketbox/iona/iona.fbx`,
        gender: 'female',
        voice: { pitch: 1.08, rate: 1.02, preferredNames: ['Jenny', 'Aria', 'Zira', 'Samantha'] },
        archiveVisible: true,
        courtVisible: true,
        tint: '#33444a',
        accent: '#86aeb1',
        summary: 'Exacting and honest by instinct, but terrified of admitting uncertainty in her own audit.',
        tell: 'She checks her brass chronometer before making any claim about time.',
        position: [-5.3, 0, -3.6],
        rotation: 0.7,
      },
      197,
    ),
    character(
      {
        id: 'silas',
        name: 'Silas Venn',
        role: 'Licensed courier · Material witness',
        shortRole: 'The courier',
        identity: 'hotei|silas-venn',
        portrait: `${ASSET_BASE}assets/characters/silas-venn.png`,
        model: `${ASSET_BASE}assets/models/rocketbox/silas/silas.fbx`,
        gender: 'male',
        voice: { pitch: 0.94, rate: 1.08, preferredNames: ['Ryan', 'Mark', 'Guy', 'Alex'] },
        archiveVisible: true,
        courtVisible: true,
        tint: '#443127',
        accent: '#c6844f',
        summary: 'Quick, observant, and willing to redirect suspicion if it protects his license.',
        tell: 'He smiles before the lie and goes still immediately after it.',
        position: [5.4, 0, -3.8],
        rotation: -0.7,
      },
      163,
    ),
    character(
      {
        id: 'orin',
        name: 'Orin Sable',
        role: 'Archive director · Deceased',
        shortRole: 'The victim',
        identity: 'hotei|orin-sable',
        portrait: `${ASSET_BASE}assets/characters/orin-sable.png`,
        model: `${ASSET_BASE}assets/models/rocketbox/orin/orin.fbx`,
        gender: 'male',
        voice: { pitch: 0.83, rate: 0.9, preferredNames: ['Daniel', 'David', 'Guy', 'Alex'] },
        archiveVisible: false,
        courtVisible: false,
        tint: '#293038',
        accent: '#d9d2c3',
        summary: 'He discovered unauthorized witness-memory edits and arranged a private confrontation.',
        tell: 'His last emergency imprint survived as three sensory fragments.',
        position: [0, 0, 0.8],
        rotation: 0,
      },
      209,
    ),
    character(
      {
        id: 'judge',
        name: 'Justice Mara Voss',
        role: 'Presiding justice · Tribunal of Echoes',
        shortRole: 'The bench',
        identity: 'hotei|mara-voss',
        portrait: `${ASSET_BASE}assets/characters/mara-voss.png`,
        model: `${ASSET_BASE}assets/models/rocketbox/judge/judge.fbx`,
        gender: 'female',
        voice: { pitch: 0.84, rate: 0.86, preferredNames: ['Sonia', 'Aria', 'Zira', 'Samantha'] },
        archiveVisible: false,
        courtVisible: true,
        tint: '#1b2025',
        accent: '#d5b06d',
        summary: 'A feared proceduralist who will tolerate spectacle only when it uncovers material truth.',
        tell: 'She lowers her voice—not raises it—when the court is one breath from losing control.',
        position: [0, 1.42, -5.55],
        rotation: Math.PI,
      },
      243,
    ),
    character(
      {
        id: 'bailiff',
        name: 'Marshal Kade Arden',
        role: 'Court marshal · Tribunal security',
        shortRole: 'The marshal',
        identity: 'hotei|kade-arden',
        portrait: `${ASSET_BASE}assets/characters/kade-arden.png`,
        model: `${ASSET_BASE}assets/models/rocketbox/bailiff/bailiff.fbx`,
        gender: 'male',
        voice: { pitch: 0.86, rate: 0.94, preferredNames: ['Davis', 'Guy', 'David', 'Alex'] },
        archiveVisible: false,
        courtVisible: true,
        tint: '#222b31',
        accent: '#7fb2bb',
        summary: 'Unshowy, watchful, and already moving before anyone else notices danger.',
        tell: 'His stance angles toward whichever person has stopped blinking.',
        position: [-6.35, 0, -1.9],
        rotation: Math.PI * 0.7,
      },
      226,
    ),
  ],
  evidence: [
    {
      id: 'charge-sheet',
      number: '00',
      title: 'Indictment seal',
      category: 'Court record',
      summary: 'Lyra Vale is charged because her keyprint is the only logged entry during the death window.',
      detail: 'The indictment assumes the archive clock, the chamber log, and physical time are equivalent.',
      reliability: 'testimonial',
      timestamp: 'Filed 07:10',
      source: 'Tribunal clerk',
      color: '#d2a65e',
      initial: true,
    },
    {
      id: 'access-log',
      number: '01',
      title: 'Chamber access log',
      category: 'Digital record',
      summary: 'A print attributed to Lyra entered Archive Nine at 21:14.',
      detail: 'The record signs its own clock source, but not whether that clock was calibrated correctly.',
      reliability: 'verified',
      timestamp: '21:14 archive time',
      source: 'Main door terminal',
      position: [-7.84, 1.15, -1.8],
      color: '#91b8c5',
      inspectLabel: 'Main door terminal',
    },
    {
      id: 'calibration-memo',
      number: '02',
      title: 'Calibration memorandum',
      category: 'Maintenance paper',
      summary: 'Archive Nine received an unscheduled clock calibration before the incident.',
      detail: 'The completion field is blank. Iona Mercer countersigned the work request, not the result.',
      reliability: 'physical',
      timestamp: '20:51 court time',
      source: 'Audit console tray',
      position: [-3.4, 0.82, -6.15],
      color: '#c7bdac',
      inspectLabel: 'Loose memorandum',
    },
    {
      id: 'chronometer',
      number: '03',
      title: "Iona's chronometer",
      category: 'Reference clock',
      summary: 'Its sealed court-time buffer is exactly seven minutes behind the archive log.',
      detail: 'The seal predates the incident. Adjusting it would leave a fracture line that is not present.',
      reliability: 'verified',
      timestamp: '21:24 court time',
      source: 'Iona Mercer',
      position: [-5.8, 0.84, -4.4],
      color: '#8fb6b7',
      inspectLabel: 'Brass chronometer',
    },
    {
      id: 'thermal-strip',
      number: '04',
      title: 'Camera thermal strip',
      category: 'Forensic trace',
      summary: 'A security-grade maintenance pulse blanked the cameras for seven minutes.',
      detail: 'Only the prefect credential tier can emit this exact thermal signature.',
      reliability: 'verified',
      timestamp: '21:10 court time',
      source: 'North camera housing',
      position: [2.9, 2.05, -7.02],
      color: '#d07a53',
      inspectLabel: 'Scorched camera housing',
    },
    {
      id: 'brass-dust',
      number: '05',
      title: 'Brass transfer dust',
      category: 'Physical trace',
      summary: "Brass powder on Orin's sleeve matches the analog service hatch, not the main door.",
      detail: 'The service hatch has no digital sensor and opens behind the central vault.',
      reliability: 'physical',
      timestamp: 'Collected 21:31',
      source: 'Orin Sable / service hatch',
      position: [7.83, 1.42, -0.8],
      color: '#c4934c',
      inspectLabel: 'Analog service hatch',
    },
    {
      id: 'blue-splinters',
      number: '06',
      title: 'Blue ceramic splinters',
      category: 'Forensic trace',
      summary: "Microscopic blue ceramic fragments were recovered from Orin's wound.",
      detail: 'The curve and glaze match an insulated phase-key housing, not archive architecture.',
      reliability: 'physical',
      timestamp: 'Collected 21:33',
      source: 'Victim perimeter',
      position: [0.9, 0.08, 1.7],
      color: '#6395bd',
      inspectLabel: 'Blue fragments',
    },
    {
      id: 'delivery-seal',
      number: '07',
      title: 'Courier delivery seal',
      category: 'Chain of custody',
      summary: "A phase-key housing was delivered under Rook Halden's private security cipher.",
      detail: 'Silas can obscure the recipient in testimony, but the physical cipher is recipient-bound.',
      reliability: 'verified',
      timestamp: '21:05 court time',
      source: "Silas Venn's delivery case",
      position: [5.9, 0.72, -4.6],
      color: '#bd7f4f',
      inspectLabel: 'Sealed delivery case',
    },
    {
      id: 'emergency-imprint',
      number: '08',
      title: "Orin's emergency imprint",
      category: 'Damaged memory',
      summary: 'Three surviving fragments: three knocks, ozone, and a polished silver cuff.',
      detail: 'The imprint identifies sensory facts, not a face. Its confidence is 0.71.',
      reliability: 'inferred',
      timestamp: '21:17 court time',
      source: 'Memory shard plinth',
      position: [0, 1.12, -0.2],
      color: '#b5d7e8',
      inspectLabel: 'Fractured memory shard',
    },
  ],
  statements: [
    {
      id: 'lyra-never-entered',
      characterId: 'lyra',
      text: 'I did not enter Archive Nine after sunset. The record cannot be mine.',
      pressText: 'Lyra looks at the amber pin. “I cannot explain the print without condemning someone who is already gone.”',
      pressureLines: [
        'Lyra looks at the amber pin. “I cannot explain the print without condemning someone who is already gone.”',
        '“The keyprint is mine. The time is not.” Her thumb presses hard enough to whiten against the pin.',
        '“I entered at 20:48 to recover my sister’s memory. I left before Orin summoned Halden.” The forbidden visit is now on the record.',
      ],
      pressureReaction: 'Her answer is incomplete, but the fear is attached to the visit—not the murder.',
      contradictionEvidenceId: 'access-log',
      contradictionTitle: 'A lie is not a murder.',
      contradictionText: 'The keyprint proves Lyra entered. It does not prove when in court time—or that she was present at death.',
    },
    {
      id: 'silas-recipient',
      characterId: 'silas',
      text: 'The phase-key shipment was marked for Vale. I left it with archive staff.',
      pressText: 'Silas smiles too early. “A courier remembers routes, counsel. Not every cipher.”',
      pressureLines: [
        'Silas smiles too early. “A courier remembers routes, counsel. Not every cipher.”',
        'The smile disappears. “The recipient wore security grey. That is all I am prepared to say.”',
        '“Halden met me at the service corridor. He made me scan the package as archive inventory.” Silas finally names the recipient.',
      ],
      pressureReaction: 'His shoulders settle only after he has named Lyra.',
      contradictionEvidenceId: 'delivery-seal',
      contradictionTitle: 'The cipher names its owner.',
      contradictionText: "The recipient-bound seal carries Rook's private security cipher. Silas redirected suspicion to protect his license.",
    },
    {
      id: 'iona-synchronized',
      characterId: 'iona',
      text: 'Archive Nine was synchronized when I began my audit. Its timestamps are court time.',
      pressText: 'Iona checks her chronometer. “Synchronization is a state, not a promise. I did not complete the final comparison.”',
      pressureLines: [
        'Iona checks her chronometer. “Synchronization is a state, not a promise. I did not complete the final comparison.”',
        '“I countersigned the work order, not the completed calibration.” Her certainty gives way to procedure.',
        '“Archive time was seven minutes fast. I knew the comparison was missing and certified the room anyway.”',
      ],
      pressureReaction: 'Her certainty collapses at the edge of the word “complete.”',
      contradictionEvidenceId: 'chronometer',
      contradictionTitle: 'Seven minutes of missing truth.',
      contradictionText: "Iona's sealed chronometer proves the archive ran seven minutes ahead. Lyra's 21:14 entry occurred at 21:07 court time.",
    },
    {
      id: 'rook-no-pulse',
      characterId: 'rook',
      text: 'No maintenance pulse was authorized. The cameras failed because Vale breached the chamber.',
      pressText: 'Rook covers the silver cuff. “My credential history is irrelevant to a failure I did not initiate.”',
      pressureLines: [
        'Rook covers the silver cuff. “My credential history is irrelevant to a failure I did not initiate.”',
        '“A prefect pulse can be issued from any secured terminal.” He answers capability, not authorship.',
        '“You will not open my credential ledger in public.” For the first time, Halden raises his voice.',
      ],
      pressureReaction: 'He challenges jurisdiction instead of explaining the thermal signature.',
      contradictionEvidenceId: 'thermal-strip',
      contradictionTitle: 'The blackout required your authority.',
      contradictionText: 'The strip records a prefect-grade maintenance pulse at 21:10. Only Rook could issue it.',
    },
    {
      id: 'rook-main-door',
      characterId: 'rook',
      text: 'Archive Nine is sealed. Anyone inside must appear in the main door log.',
      pressText: 'Rook turns toward the public gallery. “A sealed room admits no imaginative route.”',
      pressureLines: [
        'Rook turns toward the public gallery. “A sealed room admits no imaginative route.”',
        'Asked about maintenance access, he repeats “main door” and refuses to describe the room itself.',
        'His eyes move to the east wall before the question is finished. The analog service hatch is the answer he will not give.',
      ],
      pressureReaction: 'He never looks toward the analog hatch.',
      contradictionEvidenceId: 'brass-dust',
      contradictionTitle: 'The room had an unlogged door.',
      contradictionText: "Brass transfer on Orin's sleeve ties the encounter to the analog service hatch, which bypasses the log entirely.",
    },
  ],
  theory: [
    {
      key: 'culprit',
      label: 'CULPRIT',
      question: 'Who caused the death of Orin Sable?',
      correctId: 'rook',
      options: [
        { id: 'rook', label: 'Rook Halden', description: 'Security authority, hidden route, recipient cipher.' },
        { id: 'lyra', label: 'Lyra Vale', description: 'False alibi and a genuine prohibited visit.' },
        { id: 'iona', label: 'Iona Mercer', description: 'Clock access and a compromised audit.' },
        { id: 'silas', label: 'Silas Venn', description: 'Delivered the weapon housing and lied.' },
      ],
    },
    {
      key: 'method',
      label: 'METHOD',
      question: 'What caused the fatal wound?',
      correctId: 'phase-key',
      options: [
        { id: 'phase-key', label: 'Phase-key housing', description: 'Blue ceramic insulation and an ozone discharge.' },
        { id: 'memory-shard', label: 'Memory shard', description: 'Sharp, fragile, and already broken.' },
        { id: 'brass-lever', label: 'Hatch lever', description: 'Matches the transfer dust but not the wound.' },
      ],
    },
    {
      key: 'motive',
      label: 'MOTIVE',
      question: 'What was the killer protecting?',
      correctId: 'memory-tampering',
      options: [
        { id: 'memory-tampering', label: 'Witness-memory tampering', description: 'Orin discovered unauthorized edits to testimony.' },
        { id: 'sister-memory', label: "Lyra's sister-memory", description: 'Explains Lyra’s lie, not Rook’s blackout.' },
        { id: 'smuggling-license', label: "Silas's courier license", description: 'Explains false testimony, not the attack.' },
      ],
    },
    {
      key: 'doubt',
      label: 'REASONABLE DOUBT',
      question: 'Which fact breaks the case against Lyra?',
      correctId: 'clock-drift',
      options: [
        { id: 'clock-drift', label: 'Seven-minute clock drift', description: 'Her logged entry predates the murder window.' },
        { id: 'courier-lie', label: "Silas's lie", description: 'Damages one witness but does not clear the timeline.' },
        { id: 'poor-memory', label: 'Damaged emergency imprint', description: 'Limits identification but not opportunity.' },
      ],
    },
  ],
  timeline: [
    { id: 'lyra-entry', time: '20:48', title: 'The forbidden visit', description: 'Lyra enters Archive Nine to copy the memory of her erased sister.', actorIds: ['lyra'], location: 'archive', durationMs: 5000 },
    { id: 'clock-drift', time: '20:58', title: 'The archive loses seven minutes', description: 'An incomplete calibration leaves Archive Nine reporting seven minutes ahead.', actorIds: ['iona'], location: 'archive', durationMs: 5000 },
    { id: 'delivery', time: '21:05', title: 'The weapon arrives', description: 'Silas delivers a phase-key housing under Rook’s private cipher.', actorIds: ['silas', 'rook'], location: 'archive', durationMs: 5000 },
    { id: 'blackout', time: '21:10', title: 'A lawful signal used unlawfully', description: 'Rook emits a prefect-grade maintenance pulse and crosses the analog hatch.', actorIds: ['rook'], location: 'archive', durationMs: 5000 },
    { id: 'confrontation', time: '21:16', title: 'The confrontation', description: 'Orin names the altered memories. Rook strikes him with the phase-key housing.', actorIds: ['rook', 'orin'], location: 'archive', durationMs: 6000 },
    { id: 'plant', time: '21:19', title: 'The false timestamp', description: 'Rook plants Lyra’s duplicated keyprint in a clock he knows is wrong.', actorIds: ['rook'], location: 'archive', durationMs: 5000 },
  ],
};

export const characterById = Object.fromEntries(CASE_001.characters.map((item) => [item.id, item]));
export const evidenceById = Object.fromEntries(CASE_001.evidence.map((item) => [item.id, item]));
export const statementById = Object.fromEntries(CASE_001.statements.map((item) => [item.id, item]));
